/**
 * #38 · Detalle de un producto: ficha, edición y baja.
 *
 * La baja es `DELETE /api/v1/products/{id}` y **desactiva, no borra** (#68).
 * Un SKU que tuvo producto en la calle no se elimina: los escaneos que ya
 * ocurrieron lo referencian y el auditor va a querer ver el catálogo tal como
 * estaba cuando se serializó ese lote. La API responde 200 con el producto ya
 * desactivado, así que acá no se navega a ninguna parte — se queda mostrando
 * la ficha con el estado nuevo, que es la confirmación de que la baja es
 * reversible y no un borrado.
 *
 * Reactivar es `PATCH { is_active: true }`, el mismo camino que usa el
 * formulario de edición.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useApiQuery } from '@/api/hooks';
import { batchKeys, productKeys } from '@/api/keys';
import type { Schemas } from '@/api/types';
import { useApi } from '@/api/useApi';
import { useAuth } from '@/auth/useAuth';
import { useI18n } from '@/i18n/useI18n';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { ErrorState, QueryBoundary } from '@/ui/states';
import { TIER_LABEL_KEYS, TIER_TONE, isSecurityTier, riskTone } from './catalog';
import { gs1ProductElement } from './gtin';
import { ProductForm, type ProductDraft } from './ProductForm';
import { Dialog } from './ui/Dialog';
import { DetailList, DetailRow } from './ui/fields';

type Product = Schemas['ProductOut'];

export function ProductDetailPage(): ReactNode {
  const { t, fmt } = useI18n();
  const { can } = useAuth();
  const api = useApi();
  const queryClient = useQueryClient();
  const params = useParams<{ productId: string }>();
  const productId = Number(params.productId);
  const valid = Number.isInteger(productId) && productId > 0;

  const [editing, setEditing] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  const product = useApiQuery<Product>(
    productKeys.detail(productId),
    `/api/v1/products/${String(productId)}`,
    undefined,
    { enabled: valid },
  );

  const update = useMutation<Product, unknown, ProductDraft>({
    mutationFn: (draft) => api.patch<Product>(`/api/v1/products/${String(productId)}`, draft),
    onSuccess: () => {
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });

  const deactivate = useMutation<Product>({
    mutationFn: () => api.delete<Product>(`/api/v1/products/${String(productId)}`),
    onSuccess: () => {
      setDeactivating(false);
      void queryClient.invalidateQueries({ queryKey: productKeys.all });
      // Los lotes muestran el SKU y el estado del producto.
      void queryClient.invalidateQueries({ queryKey: batchKeys.all });
    },
  });

  const reactivate = useMutation<Product>({
    mutationFn: () => api.patch<Product>(`/api/v1/products/${String(productId)}`, { is_active: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.all });
      void queryClient.invalidateQueries({ queryKey: batchKeys.all });
    },
  });

  if (!valid) {
    return (
      <div className="mx-auto max-w-3xl">
        <Card>
          <CardBody>
            <ErrorState error={new Error(t('products.detail.badId'))} />
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <Link to="/products" className="inline-block text-sm text-accent underline underline-offset-2">
        {t('products.back')}
      </Link>

      <QueryBoundary query={product}>
        {(data) => (
          <Card>
            <CardHeader
              title={data.name}
              description={data.sku}
              actions={
                can('analyst') ? (
                  <>
                    <Button
                      size="sm"
                      onClick={() => {
                        update.reset();
                        setEditing(true);
                      }}
                    >
                      {t('products.edit')}
                    </Button>
                    {can('admin') &&
                      (data.is_active ? (
                        <Button
                          size="sm"
                          variant="critical"
                          onClick={() => {
                            deactivate.reset();
                            setDeactivating(true);
                          }}
                        >
                          {t('products.deactivate')}
                        </Button>
                      ) : (
                        // Volver no destruye nada, así que no pasa por un
                        // diálogo de confirmación.
                        <Button
                          size="sm"
                          onClick={() => {
                            reactivate.mutate();
                          }}
                          disabled={reactivate.isPending}
                        >
                          {reactivate.isPending ? t('form.saving') : t('products.reactivate')}
                        </Button>
                      ))}
                  </>
                ) : undefined
              }
            />
            <CardBody className="space-y-4">
              {Boolean(reactivate.error) && <ErrorState error={reactivate.error} />}
              <DetailList>
                <DetailRow label={t('products.column.sku')} value={<code className="font-mono">{data.sku}</code>} />
                <DetailRow
                  label={t('products.column.gtin')}
                  value={
                    <code className="font-mono">{data.gtin ?? t('products.gtin.none')}</code>
                  }
                />
                <DetailRow
                  label={t('products.detail.gs1')}
                  value={
                    <code className="font-mono">
                      {gs1ProductElement(data.gtin) ?? t('products.gtin.none')}
                    </code>
                  }
                />
                <DetailRow
                  label={t('products.column.tier')}
                  value={
                    <Badge tone={TIER_TONE}>
                      {isSecurityTier(data.security_tier)
                        ? t(TIER_LABEL_KEYS[data.security_tier])
                        : data.security_tier}
                    </Badge>
                  }
                />
                <DetailRow
                  label={t('products.column.risk')}
                  value={
                    <Badge tone={riskTone(data.risk_score)}>{fmt.number(data.risk_score)}</Badge>
                  }
                />
                <DetailRow
                  label={t('products.column.status')}
                  value={
                    <Badge tone={data.is_active ? 'verified' : 'neutral'}>
                      {data.is_active ? t('products.active') : t('products.inactive')}
                    </Badge>
                  }
                />
                {!data.is_active && (
                  <DetailRow
                    label={t('products.column.deactivated')}
                    value={
                      data.deactivated_at
                        ? fmt.dateTime(data.deactivated_at)
                        : t('products.deactivated.unknown')
                    }
                  />
                )}
                <DetailRow label={t('products.column.created')} value={fmt.dateTime(data.created_at)} />
                <DetailRow label={t('products.column.updated')} value={fmt.dateTime(data.updated_at)} />
              </DetailList>

              <p className="text-sm">
                <Link
                  to={`/batches?product=${String(data.id)}`}
                  className="text-accent underline underline-offset-2"
                >
                  {t('products.detail.batches')}
                </Link>
              </p>
            </CardBody>

            <Dialog
              open={editing}
              onClose={() => {
                setEditing(false);
              }}
              title={t('products.edit.title')}
              description={data.sku}
            >
              <ProductForm
                product={data}
                onSubmit={(draft) => {
                  update.mutate(draft);
                }}
                onCancel={() => {
                  setEditing(false);
                }}
                isSubmitting={update.isPending}
                error={update.error}
              />
            </Dialog>

            <Dialog
              open={deactivating}
              onClose={() => {
                setDeactivating(false);
              }}
              tone="critical"
              title={t('products.deactivate.title')}
              description={t('products.deactivate.description')}
              footer={
                <>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setDeactivating(false);
                    }}
                    disabled={deactivate.isPending}
                  >
                    {t('form.cancel')}
                  </Button>
                  <Button
                    variant="critical"
                    onClick={() => {
                      deactivate.mutate();
                    }}
                    disabled={deactivate.isPending}
                  >
                    {deactivate.isPending ? t('form.saving') : t('products.deactivate.confirm')}
                  </Button>
                </>
              }
            >
              <p className="text-sm text-ink">
                {t('products.deactivate.detail', { sku: data.sku, name: data.name })}
              </p>
              {Boolean(deactivate.error) && (
                <div className="mt-3">
                  <ErrorState error={deactivate.error} />
                </div>
              )}
            </Dialog>
          </Card>
        )}
      </QueryBoundary>
    </div>
  );
}
